import styled, { css } from 'styled-components';

import { rgba } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import {
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { styledGetCustomComponentProperty } from '../../helpers';
import { fontStyle } from '../../theme/helpers/fontStyle';

import { FIELD_ALIGN } from './Field.types';

const transitionMixin = css`
  transition-duration: 300ms;
`;

export const $InlineFieldLabelNewUi = styled.span`
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  ${fontStyle('xs')};
  font-weight: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.DEFAULT_LABEL,
    property: SUPPORTED_PROPERTIES.FONT_WEIGHT,
  })};

  svg {
    height: ${({ theme }) => theme.fonts.size.ultraSmall};
    max-width: ${({ theme }) => theme.fonts.size.ultraSmall};
    margin-right: ${spacing('sm')};
    color: ${({ theme }) => theme.colors.paragraphTextColor};
  }

  &:after {
    content: ':';
    margin: 0 ${spacing('xs')};
  }
`;

export const $FieldLabelNewUi = styled.div<{ $align: FIELD_ALIGN }>`
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  ${fontStyle('xs')};
  font-weight: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.DEFAULT_LABEL,
    property: SUPPORTED_PROPERTIES.FONT_WEIGHT,
  })};

  display: inline-flex;
  align-items: start;
  justify-content: ${({ $align }) =>
    $align === FIELD_ALIGN.RIGHT ? 'flex-end' : null};
  > div {
    height: 100%;
  }
  svg {
    height: ${({ theme }) => theme.fonts.size.ultraSmall};
    max-width: ${({ theme }) => theme.fonts.size.ultraSmall};
    margin-right: ${spacing('sm')};
    color: ${({ theme }) => theme.colors.paragraphTextColor};
  }

  &:after {
    content: ':';
    margin: 0 ${spacing('xs')};
  }
`;

export const $FieldLabel = styled.div<{ $align: FIELD_ALIGN }>`
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  ${fontStyle('xs')};
  width: inherit;
  font-weight: ${fontWeight('medium')};
  margin-bottom: ${spacing('sm')};

  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === FIELD_ALIGN.RIGHT ? 'flex-end' : null};
  > div {
    height: 100%;
  }
  svg {
    height: ${({ theme }) => theme.fonts.size.ultraSmall};
    max-width: ${({ theme }) => theme.fonts.size.ultraSmall};
    margin-right: ${spacing('sm')};
    color: ${({ theme }) => theme.colors.paragraphTextColor};
  }
`;

const copyMixin = css`
  position: relative;
  border-radius: ${({ theme }) => theme.tw.radius.sm};

  background: ${rgba('#ddd', 0)};
  svg {
    opacity: 0;
    margin-left: ${spacing('sm')};
    &:first-child {
      margin-left: 0;
    }
    ${transitionMixin};
    transition-duration: 500ms;
    min-width: ${spacing('md')};
    transform: scale(1);
  }

  &:hover {
    svg {
      opacity: 1;
    }
    svg:hover {
      transition-duration: 100ms;
      transform: scale(1.3);
    }

    background: ${rgba('#ddd', 1)};
  }
  &:active {
    background: ${rgba('#ccc', 1)};
    user-select: none;
    svg:active {
      transition-duration: 100ms;
      transform: scale(1.6);
    }
  }
`;

export const $FieldValueContainer = styled.div<{
  $align: FIELD_ALIGN;
  $isError?: boolean;
  $isSuccess?: boolean;
  $isFluid?: boolean;
  $isElementValid?: boolean;
}>`
  display: ${({ $isElementValid }) => ($isElementValid ? 'flex' : 'inline')};
  justify-content: ${({ $align }) =>
    $align === FIELD_ALIGN.RIGHT ? 'flex-end' : null};
  border-bottom: ${({ theme, $isError, $isSuccess }) => {
    if ($isError) {
      return `3px solid ${rgba(theme.colors.danger, 0.4)}`;
    }
    if ($isSuccess) {
      return `3px solid ${rgba(theme.colors.success, 0.3)}`;
    }

    return null;
  }};
  width: ${({ $isFluid }) => ($isFluid ? '100%' : 'auto')};
  [role='button'] {
    display: inline;
  }
`;

export const $FieldValue = styled.div<{
  $hasCopy: boolean;
  hasMaxWidth: boolean;
  $noMargin: boolean;
  $isEllipsisEnabled: boolean;
  $isError?: boolean;
  $isSuccess?: boolean;
  $isHorizontal?: boolean;
  $isFluid?: boolean;
}>`
  color: ${({ theme, $isError, $isSuccess, $isHorizontal }) => {
    if ($isError) {
      return theme.colors.danger;
    }
    if ($isSuccess) {
      return theme.colors.success;
    }
    if ($isHorizontal) {
      return theme.newUI.defaultColors.text;
    }

    return theme.colors.mainTextColor;
  }};
  ${fontStyle('sm')};
  font-weight: ${({ $isError, $isSuccess }) =>
    $isError || $isSuccess ? fontWeight('bold') : fontWeight('normal')};
  ${({ $isEllipsisEnabled, $isFluid }) => {
    return $isEllipsisEnabled
      ? `
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
  `
      : `
          white-space: pre-line;
          word-break: break-word;
          overflow-wrap: break-word;
          word-wrap: break-word;
          width: ${$isFluid ? '100%' : 'fit-content'}; `;
  }};

  display: inline-flex;
  margin: 0;
  align-items: center;

  max-width: ${({ hasMaxWidth }) => (hasMaxWidth ? '100%' : 'none')};

  ${transitionMixin};
  padding: ${({ $isHorizontal }) =>
    $isHorizontal
      ? `${spacing('xxs')} ${spacing('xs')}`
      : `${spacing('xs')} ${spacing('xs')}`};
  margin: ${({ $isHorizontal }) =>
    $isHorizontal ? `0 ${spacing(1.5)}` : undefined};
  transform: translateX(-3px);
  ${({ $hasCopy }) => ($hasCopy ? copyMixin : null)};
  &:not(:last-child) {
    margin-bottom: ${({ $noMargin }) => ($noMargin ? 'unset' : spacing('lg'))};
  }
  width: ${({ $isFluid }) => ($isFluid ? '100%' : 'auto')};
`;

export const $MobileFieldContainer = styled.div`
  margin-bottom: ${spacing('sm')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const $FieldContainer = styled.div<{
  $noMargin: boolean;
  $isHorizontal: boolean;
  $isFluid?: boolean;
  $allowsTextWrap?: boolean;
  $isElementValid?: boolean;
}>`
  & ${$FieldValue} ${$FieldValue} {
    padding: ${({ $isHorizontal }) =>
      $isHorizontal ? `0 ${spacing(1.5)}` : undefined};
    margin: ${({ $isHorizontal }) =>
      $isHorizontal ? `0 ${spacing(1.5)}` : undefined};
  }
  &:not(:last-child) {
    margin-bottom: ${({ $noMargin, $isHorizontal }) => {
      if ($isHorizontal) {
        return $noMargin ? 'unset' : spacing('sm');
      }

      return $noMargin ? 'unset' : spacing(3.5);
    }};
  }

  display: ${({ $isHorizontal, $allowsTextWrap, $isElementValid }) =>
    ($isHorizontal && !$allowsTextWrap) || $isElementValid ? 'flex' : 'block'};
  align-items: ${({ $isHorizontal, $allowsTextWrap, $isElementValid }) =>
    ($isHorizontal && !$allowsTextWrap) || $isElementValid ? 'baseline' : null};

  width: ${({ $isFluid }) => ($isFluid ? '100%' : 'auto')};

  ${({ $allowsTextWrap, $isHorizontal }) =>
    $allowsTextWrap &&
    $isHorizontal &&
    css`
      ${$FieldValue} {
        display: inline;
        margin: 0;
      }
    `}
`;

export const $MaxWidthDiv = styled.div<{
  hasMaxWidth: boolean;
  $isFluid?: boolean;
  $isHorizontal?: boolean;
}>`
  max-width: ${({ hasMaxWidth }) => (hasMaxWidth ? '100%' : 'none')};
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${({ $isFluid }) => ($isFluid ? '100%' : 'auto')};
  display: inline;

  > * {
    display: ${({ $isHorizontal }) => ($isHorizontal ? 'inline' : 'block')};
  }
`;

export const $Icon = styled.div`
  margin-left: ${spacing(1.5)};
  cursor: pointer;
  display: inline-block;
`;

export const $LabelContainer = styled.div<{ $align: FIELD_ALIGN }>`
  ${({ $align }) => {
    if ($align === FIELD_ALIGN.LEFT) {
      return `margin-right: ${spacing(3.5)}`;
    }

    return `margin-left: ${spacing(3.5)}`;
  }};
`;

export const $SpannedButton = styled.button<{ $containerWidth?: number }>`
  display: flex;
  justify-content: space-between;
  height: inherit;
  align-items: center;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  text-align: left;
  white-space: ${({ $containerWidth }) =>
    $containerWidth && $containerWidth > 300 ? 'normal' : 'nowrap'};
`;
